import { useEffect, useRef, useState } from "react";

/**
 * Screens and pop-ups must appear COMPLETE — never a bare button on a black
 * page while the artwork is still downloading, and never a banner unfolding
 * line by line as it decodes.
 *
 * `preloadImage` waits for the bytes AND for `img.decode()`, so by the time we
 * render, painting is a single frame. Results are cached for the page's
 * lifetime, so a re-mount is instant.
 */
const done = new Set();
const inflight = new Map();

export function preloadImage(src) {
  if (!src || done.has(src)) return Promise.resolve();
  if (inflight.has(src)) return inflight.get(src);
  const p = new Promise((resolve) => {
    const img = new Image();
    const finish = () => {
      done.add(src);
      inflight.delete(src);
      resolve();
    };
    img.onload = () => {
      const d = img.decode ? img.decode() : Promise.resolve();
      d.catch(() => {}).then(finish);
    };
    img.onerror = finish;
    img.src = src;
  });
  inflight.set(src, p);
  return p;
}

export const isImageReady = (src) => !src || done.has(src);

/**
 * True once the image is fully decoded — or after `maxWait`, so a slow or
 * broken image can never hold a message back from the class.
 */
export function useImageReady(src, maxWait = 2500) {
  const [, bump] = useState(0);
  // Readiness is decided DURING render, not in an effect: an effect lands one
  // frame late, which is exactly long enough for a pop-up to flash with a
  // half-painted banner.
  const state = useRef({ src: undefined, ready: false });
  if (state.current.src !== src) {
    state.current = { src, ready: isImageReady(src) };
  }
  useEffect(() => {
    if (isImageReady(src)) return;
    let alive = true;
    const settle = () => {
      if (!alive || state.current.src !== src) return;
      state.current = { src, ready: true };
      bump((n) => n + 1);
    };
    const t = setTimeout(settle, maxWait);
    preloadImage(src).then(() => {
      clearTimeout(t);
      settle();
    });
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [src, maxWait]);
  return state.current.ready;
}
